import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Scales } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1543832923-44667a44c804?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBza3lsaW5lJTIwc3Vuc2V0fGVufDB8fHx8MTc4NTE1MjAyMnww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const [email, setEmail] = useState("admin@ardenleigh.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">
      {/* Hero */}
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img src={HERO} alt="London" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1528]/95 via-[#0B1528]/75 to-[#0B1528]/40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <Scales size={22} weight="light" className="text-[#C5A059]" />
            <div>
              <div className="font-serif-brand text-xl leading-tight">Arden Leigh</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-300">LLP · Immigration</div>
            </div>
          </div>

          <div className="max-w-lg">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-4">
              Case Management · London · Established 2014
            </div>
            <h2 className="font-serif-brand text-4xl lg:text-5xl leading-[1.05]">
              Precision counsel for immigration matters that shape lives.
            </h2>
            <p className="mt-6 text-sm text-slate-300 max-w-md leading-relaxed">
              An internal workspace for advicers, paralegals and caseworkers at Arden Leigh LLP.
              Manage clients, matters, deadlines and fixed-fee billing in a single, considered environment.
            </p>
          </div>

          <div className="text-[11px] tracking-wider text-slate-400">
            Authorised by the Immigration Advice Authority (IAA) · No. F201200522
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="col-span-1 lg:col-span-2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <Scales size={22} weight="light" className="text-[#0B1528]" />
            <div className="font-serif-brand text-lg">Arden Leigh LLP</div>
          </div>

          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-3">
            Staff sign in
          </div>
          <h1 className="font-serif-brand text-3xl mb-8">Welcome back.</h1>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs tracking-wider uppercase text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 rounded-sm border-slate-300 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-wider uppercase text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 rounded-sm border-slate-300 h-11"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-[#C5A059] hover:bg-[#D4AF37] text-[#0B1528] font-medium tracking-wide"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-[11px] text-slate-500 leading-relaxed">
            Access is restricted to authorised Arden Leigh LLP personnel. Unauthorised access is prohibited
            and may be logged.
          </p>
        </div>
      </div>
    </div>
  );
}
